import _objectSpread from "/home/jenkins/workspace/tServices_Forms_Client_CICD_main/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/objectSpread2";
import _defineProperty from "/home/jenkins/workspace/tServices_Forms_Client_CICD_main/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "/home/jenkins/workspace/tServices_Forms_Client_CICD_main/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "/home/jenkins/workspace/tServices_Forms_Client_CICD_main/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["onChange", "maxRows", "minRows", "style", "value", "hasError", "loading", "loadingAriaLabel", "onFocus", "onBlur"];
import { jsxs, jsx } from 'react/jsx-runtime';
import { BorderInputBorderHover, Border1, BorderInputBorderFocus, Border2, BorderInputBorderError } from '@box/blueprint-web-assets/tokens/tokens';
import clsx from 'clsx';
import { forwardRef, useRef, useState, useCallback, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { LoadingIndicator } from '../../loading-indicator/loading-indicator.js';
import { debounce } from '../../utils/debounce.js';
import { ownerWindow } from '../../utils/ownerWindow.js';
import { useEnhancedEffect } from '../../utils/useEnhancedEffect.js';
import { useForkRef } from '../../utils/useForkRef.js';
import styles from './text-area-autosize.module.js';

function convertToDecimalNumber(value) {
  return parseInt(value, 10) || 0;
}

function getBoxShadowStyle(width, color) {
  return "0px 0px 0px ".concat(width, " ").concat(color);
}

var getScrollbarWidth = function getScrollbarWidth() {
  var _outer$parentNode;

  var outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.width = '100px';
  document.body.appendChild(outer);
  var widthNoScroll = outer.offsetWidth;
  outer.style.overflow = 'scroll';
  var inner = document.createElement('div');
  inner.style.width = '100%';
  outer.appendChild(inner);
  var widthWithScroll = inner.offsetWidth;
  outer === null || outer === void 0 ? void 0 : (_outer$parentNode = outer.parentNode) === null || _outer$parentNode === void 0 ? void 0 : _outer$parentNode.removeChild(outer);
  return widthNoScroll - widthWithScroll;
};

var hiddenTextAreaStyle = {
  shadow: {
    // hide from user
    visibility: 'hidden',
    // Remove from the content flow
    position: 'absolute',
    // Ignore the scrollbar width
    overflow: 'hidden',
    height: 0,
    top: 0,
    left: 0,
    // Create a new layer, increase the isolation of the computed values
    transform: 'translateZ(0)'
  }
};

function isEmpty(obj) {
  return obj === undefined || obj === null || Object.keys(obj).length === 0 || obj.outerHeightStyle === 0 && !obj.overflow;
}

var TextAreaAutosize = /*#__PURE__*/forwardRef(function TextAreaAutosize(props, ref) {
  var _clsx;

  var onChange = props.onChange,
      maxRows = props.maxRows,
      _props$minRows = props.minRows,
      minRows = _props$minRows === void 0 ? 1 : _props$minRows,
      inlineStyle = props.style,
      value = props.value,
      hasError = props.hasError,
      _props$loading = props.loading,
      loading = _props$loading === void 0 ? false : _props$loading,
      loadingAriaLabel = props.loadingAriaLabel,
      onFocus = props.onFocus,
      onBlur = props.onBlur,
      rest = _objectWithoutProperties(props, _excluded);

  var _useRef = useRef(value != null),
      isControlled = _useRef.current;

  var inputRef = useRef(null);
  var handleRef = useForkRef(ref, inputRef);
  var shadowRef = useRef(null);
  var renders = useRef(0);

  var _useState = useState({
    outerHeightStyle: 0
  }),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isFocused = _useState4[0],
      setIsFocused = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      isScrollbarVisible = _useState6[0],
      setIsScrollbarVisible = _useState6[1];

  var isLoading = !props.disabled && loading && loadingAriaLabel;
  var focusTextArea = useCallback(function () {
    var _inputRef$current;

    (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
  }, []);
  var getUpdatedState = useCallback(function () {
    var fallbackHeight = {
      outerHeightStyle: 0
    };

    if (!inputRef.current) {
      return fallbackHeight;
    }

    var input = inputRef.current;
    var containerWindow = ownerWindow(input);
    var computedStyle = containerWindow.getComputedStyle(input); // If input's width is shrunk and it's not visible, don't sync height.

    if (computedStyle.width === '0px') {
      return fallbackHeight;
    }

    if (!shadowRef.current) {
      return fallbackHeight;
    }

    var inputShallow = shadowRef.current;
    inputShallow.style.width = computedStyle.width;
    inputShallow.value = input.value || props.placeholder || 'x';

    if (inputShallow.value.slice(-1) === '\n') {
      // Certain fonts which overflow the line height will cause the textarea
      // to report a different scrollHeight depending on whether the last line
      // is empty. Make it non-empty to avoid this issue.
      inputShallow.value += ' ';
    }

    var boxSizing = computedStyle.boxSizing;
    var padding = convertToDecimalNumber(computedStyle.paddingBottom) + convertToDecimalNumber(computedStyle.paddingTop);
    var border = convertToDecimalNumber(computedStyle.borderBottomWidth) + convertToDecimalNumber(computedStyle.borderTopWidth); // The height of the inner content

    var innerHeight = inputShallow.scrollHeight; // Measure height of a textarea with a single row

    inputShallow.value = 'x';
    var singleRowHeight = inputShallow.scrollHeight; // The height of the outer content

    var outerHeight = innerHeight;

    if (minRows) {
      outerHeight = Math.max(Number(minRows) * singleRowHeight, outerHeight);
    }

    if (maxRows) {
      outerHeight = Math.min(Number(maxRows) * singleRowHeight, outerHeight);
    }

    outerHeight = Math.max(outerHeight, singleRowHeight); // Take the box sizing into account for applying this value as a style.

    var outerHeightStyle = outerHeight + (boxSizing === 'border-box' ? padding + border : 0);
    var overflow = Math.abs(outerHeight - innerHeight) <= 1;
    return {
      outerHeightStyle: outerHeightStyle,
      overflow: overflow
    };
  }, [maxRows, minRows, props.placeholder]);

  var updateState = function updateState(prevState, newState) {
    var outerHeightStyle = newState.outerHeightStyle,
        overflow = newState.overflow; // Need a large enough difference to update the height.
    // This prevents infinite rendering loop.

    if (renders.current < 20 && (outerHeightStyle > 0 && Math.abs((prevState.outerHeightStyle || 0) - outerHeightStyle) > 1 || prevState.overflow !== overflow)) {
      renders.current += 1;
      return {
        overflow: overflow,
        outerHeightStyle: outerHeightStyle
      };
    }

    if (process.env.NODE_ENV !== 'production') {
      if (renders.current === 20) {
        /* eslint-disable-next-line no-console */
        console.error(['Too many re-renders. The layout is unstable.', 'TextAreaAutosize limits the number of renders to prevent an infinite loop.'].join('\n'));
      }
    }

    return prevState;
  };

  var syncHeight = useCallback(function () {
    var newState = getUpdatedState();

    if (isEmpty(newState)) {
      return;
    }

    setState(function (prevState) {
      return updateState(prevState, newState);
    });
  }, [getUpdatedState]);

  var syncHeightWithFlushSync = function syncHeightWithFlushSync() {
    var newState = getUpdatedState();

    if (isEmpty(newState)) {
      return;
    } // In React 18, state updates in a ResizeObserver's callback are happening after the paint which causes flickering
    // when doing some visual updates in it. Using flushSync ensures that the dom will be painted after the states updates happen
    // Related issue - https://github.com/facebook/react/issues/24331


    flushSync(function () {
      setState(function (prevState) {
        return updateState(prevState, newState);
      });
    });
  };

  useEffect(function () {
    var handleResize = debounce(function () {
      renders.current = 0; // If the TextAreaAutosize component is replaced by Suspense with a fallback, the last
      // ResizeObserver's handler that runs because of the change in the layout is trying to
      // access a dom node that is no longer there (as the fallback component is being shown instead).
      // See https://github.com/mui/material-ui/issues/32640

      if (inputRef.current) {
        syncHeightWithFlushSync();
      }
    }, 200);
    var input = inputRef.current;
    var containerWindow;
    var resizeObserver;

    if (input) {
      containerWindow = ownerWindow(input);
      containerWindow.addEventListener('resize', handleResize);

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(input);
      }
    }

    return function () {
      if (!containerWindow) {
        return;
      }

      handleResize.clear();
      containerWindow.removeEventListener('resize', handleResize);

      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  });
  useEnhancedEffect(function () {
    syncHeight();
  });
  useEffect(function () {
    renders.current = 0;
  }, [value]);

  var handleChange = function handleChange(event) {
    renders.current = 0;

    if (!isControlled) {
      syncHeight();
    }

    if (onChange) {
      onChange(event);
    }
  };
  /**
   * MacOs has system specific setting for scroll visibility
   * detect if it's always visible or not to adjust
   * relevant styling
   */


  useEffect(function () {
    setIsScrollbarVisible(getScrollbarWidth() > 0);
  }, []); // border management

  var handleBlur = function handleBlur(e) {
    setIsFocused(false);

    if (onBlur) {
      onBlur(e);
    }
  };

  var handleFocus = function handleFocus(e) {
    setIsFocused(true);

    if (onFocus) {
      onFocus(e);
    }
  };

  var handleHover = function handleHover(e) {
    if (isFocused || rest.disabled || hasError) {
      return;
    }

    e.currentTarget.style.boxShadow = getBoxShadowStyle(Border1, BorderInputBorderHover);
  };

  var handleOnMouseOut = function handleOnMouseOut(e) {
    if (isFocused || rest.disabled || hasError) {
      return;
    }

    e.currentTarget.style.boxShadow = '';
  };

  var borderInlineStyle = {};

  if (isFocused) {
    borderInlineStyle = {
      boxShadow: getBoxShadowStyle(Border2, BorderInputBorderFocus)
    };
  } else if (hasError) {
    borderInlineStyle = {
      boxShadow: getBoxShadowStyle(Border2, BorderInputBorderError)
    };
  }

  var classNames = clsx([styles.textArea], (_clsx = {}, _defineProperty(_clsx, styles.error, hasError), _defineProperty(_clsx, styles.loading, isLoading), _clsx));
  var shouldCompensateScrollbar = !state.overflow && !isScrollbarVisible && (hasError || isLoading);
  return jsxs("div", {
    className: styles.border,
    onMouseEnter: handleHover,
    onMouseLeave: handleOnMouseOut,
    style: borderInlineStyle,
    children: [jsx("textarea", _objectSpread(_objectSpread({}, rest), {}, {
      ref: handleRef,
      className: classNames,
      onBlur: handleBlur,
      onChange: handleChange,
      onFocus: handleFocus,
      // Apply the rows prop to get a "correct" first SSR paint
      rows: minRows,
      style: _objectSpread(_objectSpread({
        height: state.outerHeightStyle,
        // Need a large enough difference to allow scrolling.
        // This prevents infinite rendering loop.
        overflow: state.overflow ? 'hidden' : undefined
      }, shouldCompensateScrollbar ? {
        paddingInlineEnd: '3rem'
      } : {}), inlineStyle),
      value: value
    })), jsx("textarea", {
      ref: shadowRef,
      "aria-hidden": true,
      className: classNames,
      readOnly: true,
      style: _objectSpread(_objectSpread(_objectSpread({}, hiddenTextAreaStyle.shadow), inlineStyle), {}, {
        padding: 0
      }),
      tabIndex: -1
    }), isLoading && jsx(LoadingIndicator, {
      "aria-label": loadingAriaLabel,
      className: styles.loadingIndicator,
      onClick: focusTextArea,
      style: !state.overflow ? {
        right: '0.75rem'
      } : {}
    })]
  });
});
export { TextAreaAutosize };